// Range rings and transit estimates for the COP map.
//
// Pure geometry, thresholds and formatting - no React, no MapLibre, so every
// rule here is unit-testable. The layers that draw these live in
// droneMapLayers.ts; the features are built in droneMapGeoJson.ts.
//
// All geo math routes through mission-planning.ts (haversineDistanceM,
// estimateFlightTimeS) so a transit estimate and a planned route can never
// disagree about how long a leg takes.

import {
  DEFAULT_SPEED_MPS,
  createWaypoint,
  estimateFlightTimeS,
  haversineDistanceM,
  type LatLng,
} from './mission-planning';

/**
 * The ring set, in metres. Three rings only: the operator is reading a fleet
 * picture, not a range chart, and every extra ring spends salience budget that
 * belongs to live tracks. 1/2/5 km covers the band a multirotor sortie actually
 * occupies - the planner already warns past 2 km from a dock
 * (DOCK_RANGE_WARNING_M).
 */
export const RANGE_RING_RADII_M = [1000, 2000, 5000] as const;

/** Vertices per ring. 64 keeps a 5 km ring smooth at operational zoom. */
export const RANGE_RING_STEPS = 64;

/**
 * Below this groundspeed the aircraft is hovering, loitering or being pushed
 * around by wind, and its current speed says nothing about how fast it would
 * transit. The planner's own default takes over, and the label reports which
 * speed the estimate actually used.
 */
export const MIN_CREDIBLE_GROUNDSPEED_MPS = 1;

// Flat-earth metres-per-degree. Accurate to well under a metre at ring scale
// (<= 5 km), which is finer than the GPS fix the ring is centred on.
const M_PER_DEG_LAT = 110540;
const M_PER_DEG_LNG = 111320;

/**
 * Closed ring of [lng, lat] pairs approximating a circle of `radiusM` around a
 * point. First and last vertex coincide, so it serves as a LineString ring or a
 * Polygon outer ring.
 */
export function circleRingCoords(
  lat: number,
  lng: number,
  radiusM: number,
  steps = 32,
): [number, number][] {
  const coords: [number, number][] = [];
  const cosLat = Math.cos((lat * Math.PI) / 180);
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    coords.push([
      lng + (radiusM / M_PER_DEG_LNG) * Math.cos(angle) / cosLat,
      lat + (radiusM / M_PER_DEG_LAT) * Math.sin(angle),
    ]);
  }
  return coords;
}

/** Due-north point on a ring - where its caption is anchored. */
export function ringLabelPoint(lat: number, lng: number, radiusM: number): [number, number] {
  return [lng, lat + radiusM / M_PER_DEG_LAT];
}

/**
 * Ring caption. These are chosen round numbers, not measurements, so they read
 * as "1 KM" rather than `formatDistance`'s "1.00 km" - two decimals of false
 * precision on a nominal ring is noise.
 */
export function formatRingLabel(radiusM: number): string {
  if (radiusM < 1000) return `${Math.round(radiusM)} M`;
  const km = radiusM / 1000;
  return `${Number.isInteger(km) ? km : km.toFixed(1)} KM`;
}

/**
 * Transit time as M:SS, or H:MM:SS past an hour. Rounded UP to the second, never
 * to nearest: 90.2 s reads 1:31, so the number an operator plans against is
 * never shorter than the leg actually is.
 */
export function formatTransitDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const total = Math.ceil(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/**
 * Why a battery-derived reach ring could not be drawn. One reason today, and it
 * is structural rather than per-aircraft.
 */
export type BatteryReachUnavailableReason = 'no_endurance_model';

export const BATTERY_REACH_UNAVAILABLE_COPY: Record<BatteryReachUnavailableReason, string> = {
  no_endurance_model: 'NO REACH RING · airframe endurance not modelled',
};

export type BatteryReachResult =
  | { available: true; radiusM: number }
  | { available: false; reason: BatteryReachUnavailableReason };

/**
 * Battery-derived reach ring (the ForeFlight fuel-ring idea) - DELIBERATELY NOT
 * DRAWN, and this function is the place that says so out loud.
 *
 * Turning a state of charge into a distance needs an endurance model: minutes of
 * flight at 100% for THIS airframe, and how that degrades with payload, wind and
 * pack age. The platform has none of it. The `drones` registry carries no
 * endurance, no pack capacity and no cruise spec
 * (db/migrations/001_initial_schema.sql), and `battery_percent` is a raw pack
 * reading with no discharge curve behind it - `services/fleet-readiness.ts` uses
 * it only to RANK aircraft, never to predict range.
 *
 * A ring drawn from `battery_percent x speed` would be a number the system
 * invented, painted at map scale, around an aircraft an operator may be about to
 * send somewhere. That is the class of mark CLAUDE.md forbids, and the same
 * reason the synthesised RC-controller markers were deleted from the COP. So:
 * no ring, and the caller surfaces the reason rather than leaving a silent gap
 * that reads as "unlimited range".
 *
 * It takes no aircraft on purpose: there is no per-aircraft input that would
 * change the answer today. When endurance lands on the drone registry, this is
 * the one function to change - it grows the aircraft parameter then, and the
 * `available: true` branch of `BatteryReachResult` starts being returned.
 */
export function batteryReachRadiusM(): BatteryReachResult {
  return { available: false, reason: 'no_endurance_model' };
}

/** Which speed the transit estimate was flown at, so the label can say. */
export type TransitSpeedBasis = 'groundspeed' | 'planner_default';

export interface TransitEstimate {
  distanceM: number;
  durationS: number;
  speedMps: number;
  speedBasis: TransitSpeedBasis;
}

/**
 * Straight-leg transit from the aircraft to a point: how far, and how long at
 * the speed named in `speedBasis`.
 *
 * The duration goes through `estimateFlightTimeS` rather than a local division
 * so leg timing has one implementation shared with the route planner.
 */
export function estimateTransit(
  from: LatLng,
  to: LatLng,
  groundspeedMps: number | null | undefined,
): TransitEstimate {
  const credible =
    typeof groundspeedMps === 'number' &&
    Number.isFinite(groundspeedMps) &&
    groundspeedMps >= MIN_CREDIBLE_GROUNDSPEED_MPS;
  const speedMps = credible ? groundspeedMps : DEFAULT_SPEED_MPS;
  const leg = createWaypoint(to.lat, to.lng, { speed: speedMps });
  return {
    distanceM: haversineDistanceM(from, to),
    durationS: estimateFlightTimeS(from, [leg], speedMps),
    speedMps,
    speedBasis: credible ? 'groundspeed' : 'planner_default',
  };
}

/**
 * Map caption for a transit leg: distance, time, and the speed the time assumes.
 *
 * The speed is shown because it is an assumption, not a measurement - an ETA an
 * operator cannot audit is worse than no ETA. `~` marks the planner default so a
 * fallback can never be read as the aircraft's own telemetry.
 */
export function formatTransitLabel(estimate: TransitEstimate): string {
  const dist =
    estimate.distanceM < 1000
      ? `${Math.round(estimate.distanceM)} M`
      : `${(estimate.distanceM / 1000).toFixed(2)} KM`;
  const speed =
    estimate.speedBasis === 'groundspeed'
      ? `${estimate.speedMps.toFixed(1)} M/S`
      : `~${estimate.speedMps.toFixed(1)} M/S`;
  return `${dist} · ${formatTransitDuration(estimate.durationS)} · ${speed}`;
}
